import {useLocale, useMessages} from "i18n";
import type {ReferralIntroPrices} from "referral/referralOffer";
import {formatMoney} from "./PricingStorage";
import {COMPARISON_PICKERS} from "./comparedPlans";
import {ComparisonCell, ComparisonRow} from "./pricingComparison";
import {PlanId} from "./planCatalogue";
import {useComparedPlans} from "./useComparedPlans";
import {
	Cell,
	ColumnHeading,
	ColumnName,
	ColumnPrice,
	FeatureHeading,
	Footnote,
	Footnotes,
	FourPlanSubtitle,
	FourPlanTable,
	Frame,
	No,
	Picker,
	PickerFace,
	PickerShell,
	Section,
	Subtitle,
	TextValue,
	Title,
	TwoPlanSubtitle,
	TwoPlanTable,
	Yes,
	type ComparisonScroll,
} from "./PricingCompareTable.styles";

/**
 * "Compare every feature" table (design `.cmp`, `Transferify.dc.html` lines 621–633 + 899–901).
 * Dumb: it renders the column headers plus the {@link ComparisonRow} matrix. The Plus column is
 * highlighted with an opaque card base layered under the accent tint. Colors are all
 * `theme.tokens` so light + dark fall out for free. Cells render as a high-contrast ✓ on a locally
 * layered signal tint, a secondary-ink dash, or a mono value.
 *
 * It renders the matrix twice, and the section's width shows one (kitecorp/transferify-ui#4018): every plan
 * side by side where the four columns fit, or two plans, each chosen in a picker, where they would not, each
 * with its own subtitle. A container query on the section decides, so the page's prerendered HTML is already the
 * right one for the screen it opens on, and nothing ever scrolls sideways.
 */
export interface CompareColumn {
	id: PlanId;
	name: string;
	/** Header sub-price, e.g. "€12/mo". */
	price: string;
}

interface Props {
	columns: CompareColumn[];
	rows: ComparisonRow[];
	/** Canonical id of the highlighted column (the popular tier — design `.colpop`). */
	highlightPlanId: PlanId;
	/** Which scroller the plan headers stick to: the dialog's modal body, or the page (see {@link ComparisonScroll}). */
	scroll: ComparisonScroll;
	/**
	 * The referral footnote's figures, in the money these columns are in, or null for no line.
	 *
	 * Handed down rather than resolved here: this table used to ask the build-time locale-and-host rule
	 * while the cards beside it had already swapped to the api's storefront, so a GB reader read a euro
	 * promise over pound prices.
	 */
	referralIntro: ReferralIntroPrices | null;
	/**
	 * The id the comparison carries so a link can open the page at it (`FREE_PLAN_ANCHOR_ID`), and whose fragment
	 * opens the two-plan view on Basic and Free; none when absent.
	 */
	freePlanAnchorId?: string;
	/** Whether the two-plan view leads with its Price row (`comparisonShowsPrices`): not during a welcome run. */
	showsPrices: boolean;
}

interface MatrixProps {
	columns: CompareColumn[];
	rows: ComparisonRow[];
	highlightPlanId: PlanId;
}

function CellValue({cell, includedLabel, notIncludedLabel}: {cell: ComparisonCell; includedLabel: string; notIncludedLabel: string}) {
	if (cell.kind === "yes") return <Yes aria-label={includedLabel}>✓</Yes>;
	if (cell.kind === "no") return <No aria-label={notIncludedLabel}>—</No>;
	return <TextValue>{cell.value}</TextValue>;
}

/** Every plan side by side, each heading naming its plan and price. */
function FourPlanMatrix({columns, rows, highlightPlanId}: MatrixProps) {
	const {compare} = useMessages().pricing;
	return (
		<FourPlanTable>
			<thead>
				<tr>
					<FeatureHeading scope="col">{compare.featureHeader}</FeatureHeading>
					{columns.map((column) => (
						<ColumnHeading key={column.id} scope="col" $highlight={column.id === highlightPlanId}>
							<ColumnName>{column.name}</ColumnName>
							<ColumnPrice>{column.price}</ColumnPrice>
						</ColumnHeading>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={row.id}>
						<FeatureHeading scope="row">{row.label}</FeatureHeading>
						{columns.map((column) => (
							<Cell key={column.id} $highlight={column.id === highlightPlanId}>
								<CellValue cell={row[column.id]} includedLabel={compare.included} notIncludedLabel={compare.notIncluded}/>
							</Cell>
						))}
					</tr>
				))}
			</tbody>
		</FourPlanTable>
	);
}

/**
 * Two plans side by side, each chosen in a picker that heads its column, with the price as the first row outside
 * a welcome run. The pickers list the plans in the four-plan table's order.
 */
function TwoPlanMatrix({columns, rows, highlightPlanId, freePlanAnchorId, showsPrices}: MatrixProps & {
	freePlanAnchorId?: string;
	showsPrices: boolean;
}) {
	const {compare} = useMessages().pricing;
	const {compared, choose, beginPick, endPick} = useComparedPlans(freePlanAnchorId);
	const shown = COMPARISON_PICKERS.flatMap((picker) => {
		const column = columns.find((candidate) => candidate.id === compared[picker]);
		return column ? [{picker, column}] : [];
	});
	return (
		<TwoPlanTable>
			<thead>
				<tr>
					<FeatureHeading scope="col">{compare.featureHeader}</FeatureHeading>
					{shown.map(({picker, column}) => (
						<ColumnHeading key={picker} scope="col" $highlight={column.id === highlightPlanId}>
							<PickerShell $highlight={column.id === highlightPlanId}>
								<PickerFace aria-hidden="true">{column.name}</PickerFace>
								<Picker
									aria-label={compare.pickers[picker]}
									value={column.id}
									onFocus={beginPick}
									onPointerDown={beginPick}
									onBlur={endPick}
									onChange={(event) => {
										// Narrowed to a column this table lists rather than cast to a plan id.
										const chosen = columns.find((candidate) => candidate.id === event.target.value);
										if (chosen) choose(picker, chosen.id);
									}}
								>
									{columns.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
								</Picker>
							</PickerShell>
						</ColumnHeading>
					))}
				</tr>
			</thead>
			<tbody>
				{showsPrices && (
					<tr>
						<FeatureHeading scope="row">{compare.priceRow}</FeatureHeading>
						{shown.map(({picker, column}) => (
							<Cell key={picker} $highlight={column.id === highlightPlanId}>
								<TextValue>{column.price}</TextValue>
							</Cell>
						))}
					</tr>
				)}
				{rows.map((row) => (
					<tr key={row.id}>
						<FeatureHeading scope="row">{row.label}</FeatureHeading>
						{shown.map(({picker, column}) => (
							<Cell key={picker} $highlight={column.id === highlightPlanId}>
								<CellValue cell={row[column.id]} includedLabel={compare.included} notIncludedLabel={compare.notIncluded}/>
							</Cell>
						))}
					</tr>
				))}
			</tbody>
		</TwoPlanTable>
	);
}

export function PricingCompareTable(
	{columns, rows, highlightPlanId, scroll, referralIntro, freePlanAnchorId, showsPrices}: Props,
) {
	const {compare} = useMessages().pricing;
	const {locale} = useLocale();
	return (
		<Section>
			<Title>{compare.title}</Title>
			{/*
			  * One element holding both views' subtitles, the hidden one skipped: the frame names it as its
			  * description, and a description reads a hidden child of a shown element as nothing.
			  */}
			<Subtitle id="pricing-comparison-help">
				<FourPlanSubtitle>{compare.subtitle}</FourPlanSubtitle>
				<TwoPlanSubtitle>{compare.twoPlanSubtitle}</TwoPlanSubtitle>
			</Subtitle>
			<Frame
				$scroll={scroll}
				id={freePlanAnchorId}
				role="region"
				aria-label={compare.ariaLabel}
				aria-describedby="pricing-comparison-help"
			>
				<FourPlanMatrix columns={columns} rows={rows} highlightPlanId={highlightPlanId}/>
				<TwoPlanMatrix columns={columns} rows={rows} highlightPlanId={highlightPlanId} freePlanAnchorId={freePlanAnchorId}
					showsPrices={showsPrices}/>
			</Frame>
			<Footnotes>
				{/*
				  * The referral footnote is a FUNCTION since api#1228: it states what a referred friend
				  * pays up front, and those amounts belong to the storefront rather than to the sentence.
				  * A storefront the offer is not priced in drops the line entirely instead of printing a
				  * sentence with holes in it — the same silence every other unpriced figure answers with.
				  */}
				{compare.footnotes.flatMap((note, index) => {
					if (typeof note === "string") {
						return [<Footnote key={note}>{note}</Footnote>];
					}
					return referralIntro === null ? [] : [(
						<Footnote key={`referral-${index}`}>
							{note(
								formatMoney(referralIntro.currency, referralIntro.plus, locale),
								formatMoney(referralIntro.currency, referralIntro.premium, locale),
								referralIntro.introDays,
							)}
						</Footnote>
					)];
				})}
			</Footnotes>
		</Section>
	);
}
